/* eslint-disable jsx-a11y/no-static-element-interactions */
import moment from 'moment';
import React, { useRef } from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { COLOR } from '@src/styles/color';
import LockRoundedIcon from '@mui/icons-material/LockRounded';
import { useSelector } from 'react-redux';
import {
  FREE_PACKAGE_CODES,
  PACKAGE_STUDIO_VERSION,
} from '@src/constants/package';
import useFeatureFlags from '@src/hooks/useFeatureFlags';
import { FEATURE_KEYS } from '@src/configs/featureKeys';
import { Typography, Box } from '@mui/material';
import RcTooltip from 'rc-tooltip';
import { delimitNumber } from '@src/utils/number';
import useUserInfo from '@src/hooks/useTtsUser';
import ROUTES from '@src/constants/route';
import i18n from '@src/languages';
import DetailPackageInfo from '../DetailPackageInfo';
import './index.css';

const FORMAT_DATETIME = 'DD/MM/YYYY - HH:mm';

const checkExpiredDate = (expiryDate) => {
  if (!expiryDate) return false;
  return moment().isAfter(expiryDate);
};

const PackageInfoV3 = ({ useNewSidebarV2, isSMSUser }) => {
  const { t } = useTranslation();
  const { language } = i18n;
  const history = useHistory();
  const { user } = useSelector((state) => state.auth);
  const { packageCode, packageExpiryDate } = user;

  const {
    remainingCharacters = 0,
    bonusCharacters = 0,
    lockCharacters = 0,
  } = user;
  const { ttsUser } = useUserInfo();
  const { studio } = ttsUser;
  const { cycle, topUp, custom } = studio || {};
  const {
    remainingCredits: remainingCycleCredits = 0,
    nextResetAt,
    purchasedTopUpCredits,
  } = cycle || {};
  const { remainingCredits: remainingTopUpCredits = 0 } = topUp || {};
  const { remainingCredits: remainingCustomCredits = 0 } = custom || {};

  const isFreePackage = FREE_PACKAGE_CODES.includes(user.packageCode);

  const { getFeatureValue } = useFeatureFlags();

  const useLockCycle = getFeatureValue(FEATURE_KEYS.LOCK_CYCLE_CREDITS, {
    userId: user.id,
    email: user.email,
    phoneNumber: user.phoneNumber,
  });

  const useLockTopUp = getFeatureValue(FEATURE_KEYS.LOCK_TOP_UP_CREDITS, {
    userId: user.id,
    email: user.email,
    phoneNumber: user.phoneNumber,
  });

  const useLockCustom = getFeatureValue(FEATURE_KEYS.LOCK_CUSTOM_CREDITS, {
    userId: user.id,
    email: user.email,
    phoneNumber: user.phoneNumber,
  });

  const componentRef = useRef(null);

  const isExpiredPackage = checkExpiredDate(packageExpiryDate);

  const getPackageExpired = () => {
    const isOldPackageVersion =
      packageCode?.includes(PACKAGE_STUDIO_VERSION.V1) ||
      packageCode?.includes(PACKAGE_STUDIO_VERSION.V2) ||
      packageCode?.includes(PACKAGE_STUDIO_VERSION.V2_1);

    if (isExpiredPackage && !isOldPackageVersion) return t('outOfDate');
    if (
      isExpiredPackage &&
      !packageCode?.includes('V3') &&
      lockCharacters === 0
    )
      return t('outOfDate');

    if (!packageExpiryDate && packageCode)
      return `${t('exp')}: ${t('endless')}`;

    if (nextResetAt)
      return `${t('exp')}: ${moment(nextResetAt).format(FORMAT_DATETIME)}`;

    if (packageExpiryDate)
      return `${t('exp')}: ${moment(packageExpiryDate).format(
        FORMAT_DATETIME,
      )}`;

    return '';
  };

  const totalCharacters =
    remainingCharacters +
    bonusCharacters +
    remainingCycleCredits +
    remainingTopUpCredits +
    remainingCustomCredits;

  const viewIconLockCycle = () => {
    const isLockedCycle =
      useLockCycle &&
      remainingCycleCredits &&
      !isFreePackage &&
      ttsUser?.studio?.cycle?.isLocked;
    const isLockedCustom =
      useLockCustom &&
      remainingCustomCredits &&
      ttsUser?.studio?.custom?.isLocked;
    if (isLockedCycle || isLockedCustom) {
      return (
        <LockRoundedIcon
          style={{
            color: COLOR.redPrimary,
            fontSize: '16px',
            margin: '0px 2px',
          }}
        />
      );
    }

    if (
      useLockTopUp &&
      remainingTopUpCredits &&
      !isFreePackage &&
      ttsUser?.studio?.topUp?.isLocked
    ) {
      return (
        <LockRoundedIcon
          style={{
            color: COLOR.redPrimary,
            fontSize: '16px',
            margin: '0px 2px',
          }}
        />
      );
    }

    return <></>;
  };

  return (
    <RcTooltip
      placement="topRight"
      overlay={
        <DetailPackageInfo
          useLockCycle={useLockCycle}
          useLockTopUp={useLockTopUp}
          useLockCustom={useLockCustom}
          purchasedTopUpCredits={purchasedTopUpCredits}
          getPackageExpired={getPackageExpired}
          isSMSUser={isSMSUser}
          isExpiredPackage={isExpiredPackage}
          remainingCycleCredits={remainingCycleCredits}
          remainingTopUpCredits={remainingTopUpCredits}
          remainingCustomCredits={remainingCustomCredits}
          remainingCharacters={remainingCharacters}
          lockCharacters={lockCharacters}
          bonusCharacters={bonusCharacters}
        />
      }
      trigger={['hover']}
      showArrow={false}
      align={{
        offset: [0, 5],
      }}
    >
      <div
        ref={componentRef}
        className="wallet-package hover-wallet-package"
        onClick={() => history.push(ROUTES.SUBSCRIPTION)}
      >
        {!isExpiredPackage && (
          <Typography
            className={`remain-characters ${
              totalCharacters === 0 && 'danger-text'
            }`}
          >
            {t('remainCharacters', {
              numberCharacters: delimitNumber(totalCharacters, language),
            })}
            {viewIconLockCycle()}
          </Typography>
        )}
        {isExpiredPackage && (
          <Box className="characters-wrapper">
            <Typography
              className={`remain-characters ${
                useNewSidebarV2 && lockCharacters === 0 && 'danger-text'
              }`}
            >
              {t('stillLockCharacters', {
                numberCharacters: delimitNumber(lockCharacters, language),
              })}
              {viewIconLockCycle()}
            </Typography>
          </Box>
        )}

        <Typography
          className={`expired-date ${isExpiredPackage && 'danger-text'}`}
        >
          {getPackageExpired()}
        </Typography>
      </div>
    </RcTooltip>
  );
};

export default PackageInfoV3;
